import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

type TooltipPlacement = 'top' | 'bottom';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  className?: string;
  placement?: TooltipPlacement;
  onlyWhenTruncated?: boolean;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

const Tooltip: React.FC<TooltipProps> = ({ content, children, className, placement = 'top', onlyWhenTruncated = false }) => {
  const targetRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const hasContent = Boolean(content && content.trim());

  const isTruncated = () => {
    const el = targetRef.current;
    if (!el) return false;
    // For single-line ellipsis elements, scrollWidth will exceed clientWidth when truncated.
    return el.scrollWidth > el.clientWidth + 1;
  };

  const updatePosition = () => {
    const target = targetRef.current;
    const tooltip = tooltipRef.current;
    if (!target || !tooltip) return;

    const rect = target.getBoundingClientRect();
    const tRect = tooltip.getBoundingClientRect();
    const gap = 10;

    const centerX = rect.left + rect.width / 2;
    const x = clamp(centerX - tRect.width / 2, 10, window.innerWidth - tRect.width - 10);

    const preferTop = placement === 'top';
    const topY = rect.top - tRect.height - gap;
    const bottomY = rect.bottom + gap;
    const y = preferTop
      ? (topY >= 8 ? topY : bottomY)
      : (bottomY + tRect.height <= window.innerHeight - 8 ? bottomY : topY);

    setPos({ x, y: clamp(y, 8, window.innerHeight - tRect.height - 8) });
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();

    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();

    // capture=true to catch scroll on nested containers
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, content, placement]);

  const tooltipNode = useMemo(() => {
    if (!open || !hasContent) return null;
    return createPortal(
      <div
        ref={tooltipRef}
        className="ms-tooltip"
        style={{ left: `${pos.x}px`, top: `${pos.y}px` }}
        role="tooltip"
      >
        {content}
      </div>,
      document.body
    );
  }, [open, hasContent, content, pos.x, pos.y]);

  return (
    <>
      <span
        ref={targetRef}
        className={className}
        onMouseEnter={() => {
          if (!hasContent) return;
          if (onlyWhenTruncated && !isTruncated()) return;
          setOpen(true);
          // allow tooltip to mount, then measure
          window.setTimeout(updatePosition, 0);
        }}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => {
          if (!hasContent) return;
          if (onlyWhenTruncated && !isTruncated()) return;
          setOpen(true);
          window.setTimeout(updatePosition, 0);
        }}
        onBlur={() => setOpen(false)}
        tabIndex={hasContent && !onlyWhenTruncated ? 0 : -1}
      >
        {children}
      </span>
      {tooltipNode}
    </>
  );
};

export default Tooltip;

